import React from "react";
import {BankOutlined} from "@ant-design/icons";
import {Input, InputSizes, Select, SelectSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import {DD_ACCOUNT_TYPE_OPTIONS, DD_SOURCE_OPTIONS} from "../uploads-simulator.constants";
import styles from "../styles/sor-composer.module.scss";

/**
 * SOR direct-deposit account section plus the FI override sub-block used to
 * demo the `fi_owned_routing_match` silent signal (upload a document whose
 * routing number matches the override).
 *
 * @param {Object} props
 * @param {Object} props.account — `sor.direct_deposit_account`
 * @param {Object} props.fiOverrides — `sor.fi_overrides`
 * @param {Record<string, string>} [props.errors]
 * @param {boolean} [props.filled]
 * @param {(account: Object) => void} props.onAccountChange
 * @param {(fiOverrides: Object) => void} props.onFiOverridesChange
 */
function SorDdAccountSection({account, fiOverrides, errors, filled, onAccountChange, onFiOverridesChange}) {
    const accountValue = account || {};
    const overridesValue = fiOverrides || {};
    const setAccount = (field, value) => onAccountChange({...accountValue, [field]: value});
    const setOverride = (field, value) => onFiOverridesChange({...overridesValue, [field]: value});

    return (
        <ComposerSection icon={<BankOutlined />} title="Direct Deposit & FI Override" filled={filled} defaultCollapsed>
            <div className={styles.fieldGrid}>
                <Input
                    label="Bank name"
                    size={InputSizes.MEDIUM}
                    placeholder="First Sample Bank"
                    value={accountValue.bank_name || ""}
                    onChange={event => setAccount("bank_name", event.target.value)}
                />
                <Input
                    label="Routing number"
                    size={InputSizes.MEDIUM}
                    maxLength={9}
                    placeholder="021000021"
                    status={errors?.["direct_deposit_account.routing_number"] ? "error" : ""}
                    value={accountValue.routing_number || ""}
                    onChange={event => setAccount("routing_number", event.target.value.replace(/\D/g, ""))}
                />
                <Input
                    label="Account last 4"
                    size={InputSizes.MEDIUM}
                    maxLength={4}
                    placeholder="6789"
                    status={errors?.["direct_deposit_account.account_last_four"] ? "error" : ""}
                    value={accountValue.account_last_four || ""}
                    onChange={event => setAccount("account_last_four", event.target.value.replace(/\D/g, ""))}
                />
                <Select
                    label="Account type"
                    size={SelectSizes.MEDIUM}
                    allowClear
                    options={DD_ACCOUNT_TYPE_OPTIONS}
                    value={accountValue.type || undefined}
                    onChange={type => setAccount("type", type || "")}
                />
                <Select label="Source" size={SelectSizes.MEDIUM} options={DD_SOURCE_OPTIONS} value={accountValue.source || "real"} onChange={source => setAccount("source", source)} />
            </div>
            {errors?.["direct_deposit_account.routing_number"] ? (
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.fieldError}>
                    {errors["direct_deposit_account.routing_number"]}
                </Text>
            ) : null}

            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.subheading}>
                FI override — bank-match demo
            </Text>
            <div className={styles.fieldGrid}>
                <Input label="FI name" size={InputSizes.MEDIUM} placeholder="Members Own CU" value={overridesValue.name || ""} onChange={event => setOverride("name", event.target.value)} />
                <Input
                    label="FI routing number"
                    size={InputSizes.MEDIUM}
                    maxLength={9}
                    status={errors?.["fi_overrides.routing_number"] ? "error" : ""}
                    value={overridesValue.routing_number || ""}
                    onChange={event => setOverride("routing_number", event.target.value.replace(/\D/g, ""))}
                />
                <Input
                    label="Synonyms (comma-separated)"
                    size={InputSizes.MEDIUM}
                    placeholder="MOCU, Members Own"
                    value={(overridesValue.synonyms || []).join(", ")}
                    onChange={event =>
                        setOverride(
                            "synonyms",
                            event.target.value
                                .split(",")
                                .map(synonym => synonym.trim())
                                .filter((synonym, index, list) => synonym || index < list.length - 1)
                        )
                    }
                />
            </div>
        </ComposerSection>
    );
}

export default SorDdAccountSection;
